// src/components/tooltip/index.tsx
import React2 from "react";
import { Tooltip as TT } from "radix-ui";
import { tooltip } from "@pinatagrams/design-system/recipes";
import { motion } from "motion/react";

// src/contexts/layer-context.tsx
import React from "react";

// src/contexts/layer-types.ts
var LayerType = /* @__PURE__ */ ((LayerType2) => {
  LayerType2[LayerType2["Main"] = 0] = "Main";
  LayerType2[LayerType2["Nav"] = 1] = "Nav";
  LayerType2[LayerType2["Popover"] = 2] = "Popover";
  LayerType2[LayerType2["Toasts"] = 3] = "Toasts";
  LayerType2[LayerType2["Overlay"] = 4] = "Overlay";
  return LayerType2;
})(LayerType || {});
var layerNameMap = {
  main: 0 /* Main */,
  nav: 1 /* Nav */,
  popover: 2 /* Popover */,
  toasts: 3 /* Toasts */,
  overlay: 4 /* Overlay */
};

// src/contexts/layer-context.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var LayerContext = React.createContext(null);
function Wrapper({
  children,
  layer
}) {
  const context = React.useContext(LayerContext);
  if (!context) {
    throw new Error("[Layer] Layer.Wrapper must be used within Layer");
  }
  const resolvedLayer = typeof layer === "string" ? layerNameMap[layer] : layer;
  const nextLayer = resolvedLayer >= context.layer ? resolvedLayer : context.layer;
  return /* @__PURE__ */ jsx(LayerContext.Provider, { value: { ...context, layer: nextLayer }, children });
}
function LayerRoot({ children }) {
  const baseLayerStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  };
  return /* @__PURE__ */ jsxs(LayerContext.Provider, { value: { layer: 0 /* Main */ }, children: [
    /* @__PURE__ */ jsx("style", { children: `
				[data-layer] > * {
					pointer-events: auto;
				}
			` }),
    /* @__PURE__ */ jsx("section", { id: "layer-popover", style: { ...baseLayerStyle, zIndex: 2 }, "data-layer": "popover" }),
    /* @__PURE__ */ jsx("section", { id: "layer-toasts", style: { ...baseLayerStyle, zIndex: 3 }, "data-layer": "toasts" }),
    /* @__PURE__ */ jsx(
      "section",
      {
        id: "layer-overlay",
        style: {
          ...baseLayerStyle,
          zIndex: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        },
        "data-layer": "overlay"
      }
    ),
    children
  ] });
}
function useLayerContainer(overrideLayer) {
  const context = React.useContext(LayerContext);
  const [container, setContainer] = React.useState(void 0);
  React.useLayoutEffect(() => {
    const defaultContainer = document.getElementById("layer-popover");
    if (!defaultContainer) {
      throw new Error("[Layer] Missing default popover layer container");
    }
    if (!context) return setContainer(defaultContainer);
    let targetLayer = context.layer;
    if (overrideLayer !== void 0) {
      targetLayer = overrideLayer >= context.layer ? overrideLayer : context.layer;
    }
    if (targetLayer === 0 /* Main */ || targetLayer === 1 /* Nav */) {
      setContainer(defaultContainer);
      return;
    }
    const layerId = LayerType[targetLayer].toLowerCase();
    const element = document.getElementById(`layer-${layerId}`);
    if (!element) {
      throw new Error(`[Layer] Missing container for layer: ${layerId}`);
    }
    setContainer(element);
  }, [context, overrideLayer]);
  return container;
}
var Layer = Object.assign(LayerRoot, { Wrapper });

// src/components/tooltip/index.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var TooltipProvider = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx2(TT.Provider, { ...props, children });
};
var TooltipRoot = ({
  children,
  open,
  onOpenChange,
  delayDuration = 0
}) => {
  const [isOpen, setIsOpen] = React2.useState(open ?? false);
  React2.useEffect(() => {
    if (open !== void 0) {
      setIsOpen(open);
    }
  }, [open]);
  const handleOpenChange = (newOpen) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  return /* @__PURE__ */ jsx2(TT.Root, { delayDuration, open: isOpen, onOpenChange: handleOpenChange, children });
};
var TooltipTrigger = ({ children }) => {
  const triggerElement = React2.isValidElement(children) ? children : /* @__PURE__ */ jsx2("span", { children });
  return /* @__PURE__ */ jsx2(TT.Trigger, { asChild: true, children: triggerElement });
};
var TooltipContent = ({
  children,
  side = "top",
  sideOffset = 10,
  cardClassName,
  ...props
}) => {
  const container = useLayerContainer(2 /* Popover */);
  const MotionContent = React2.forwardRef((motionProps, ref) => /* @__PURE__ */ jsx2(motion.div, { ref, ...motionProps }));
  MotionContent.displayName = "MotionContent";
  return /* @__PURE__ */ jsx2(TT.Portal, { container, children: /* @__PURE__ */ jsx2(TT.Content, { side, sideOffset, ...props, children: /* @__PURE__ */ jsxs2(
    MotionContent,
    {
      className: tooltip().root,
      initial: { opacity: 0, y: 20, scale: 0.8 },
      animate: { opacity: 1, y: 0, scale: 1 },
      exit: {
        opacity: 0,
        y: 20,
        scale: 0.8,
        transition: { duration: 0.1 }
      },
      children: [
        /* @__PURE__ */ jsx2("div", { className: `${tooltip().card}${cardClassName ? ` ${cardClassName}` : ""}`, children }),
        /* @__PURE__ */ jsx2(TT.Arrow, { className: "tooltip-arrow" })
      ]
    }
  ) }) });
};
var Tooltip = Object.assign(TooltipRoot, {
  Trigger: TooltipTrigger,
  Content: TooltipContent,
  Provider: TooltipProvider
});
var tooltip_default = Tooltip;

// src/components/indicator/index.tsx
import { CircleIcon } from "@phosphor-icons/react";

// src/components/icon/index.tsx
import { AccessibleIcon } from "radix-ui";
import { jsx as jsx3 } from "react/jsx-runtime";
var Icon = ({
  label,
  icon: IconComponent,
  ...props
}) => {
  return /* @__PURE__ */ jsx3(AccessibleIcon.Root, { label, children: /* @__PURE__ */ jsx3(IconComponent, { ...props }) });
};
Icon.displayName = "Icon";
var icon_default = Icon;

// src/components/dropdown-menu/index.tsx
import React5, { useCallback as useCallback2 } from "react";
import { DropdownMenu as DM } from "radix-ui";
import { AnimatePresence, motion as motion4, useMotionValue, useTransform } from "motion/react";
import { DURATION as DURATION3, EASING as EASING2, SPRING } from "@pinatagrams/design-system";
import { dropdownMenuContent } from "@pinatagrams/design-system/recipes";

// src/components/layout/index.tsx
import React3 from "react";
import { motion as motion2 } from "motion/react";
import {
  stackLayout,
  rowLayout,
  pageSection,
  pageHeader,
  actionHeader,
  gridLayout,
  mainLayout,
  backdrop,
  scrollContainer
} from "@pinatagrams/design-system/recipes";
import { DURATION, EASING } from "@pinatagrams/design-system";
import { jsx as jsx4 } from "react/jsx-runtime";
var Stack = React3.forwardRef(
  ({ gap = "md", align = "stretch", skeleton = false, className, ...props }, ref) => {
    const { root } = stackLayout({ gap, align, skeleton });
    return /* @__PURE__ */ jsx4("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Stack.displayName = "Stack";
var Inline = React3.forwardRef(
  ({ gap = "md", align = "center", justify = "start", wrap = true, className, ...props }, ref) => {
    const { root } = rowLayout({ gap, align, justify, wrap });
    return /* @__PURE__ */ jsx4("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Inline.displayName = "Inline";
var PageSection = React3.forwardRef(
  ({ maxWidth = "lg", className, ...props }, ref) => {
    const { root } = pageSection({ maxWidth });
    return /* @__PURE__ */ jsx4("section", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
PageSection.displayName = "PageSection";
var PageHeader = React3.forwardRef(
  ({ className, ...props }, ref) => {
    const { root } = pageHeader({});
    return /* @__PURE__ */ jsx4("header", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
PageHeader.displayName = "PageHeader";
var HeaderRoot = React3.forwardRef(
  ({ sticky = false, skeleton = false, className, ...props }, ref) => {
    const { root } = actionHeader({ sticky, skeleton });
    return /* @__PURE__ */ jsx4("header", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
HeaderRoot.displayName = "Header";
var HeaderTitleSection = React3.forwardRef(({ className, ...props }, ref) => {
  const { title } = actionHeader({});
  return /* @__PURE__ */ jsx4("div", { ref, className: `${title}${className ? ` ${className}` : ""}`, ...props });
});
HeaderTitleSection.displayName = "Header.Title";
var HeaderActionsSection = React3.forwardRef(({ className, ...props }, ref) => {
  const { actions } = actionHeader({});
  return /* @__PURE__ */ jsx4("div", { ref, className: `${actions}${className ? ` ${className}` : ""}`, ...props });
});
HeaderActionsSection.displayName = "Header.Actions";
var Header = Object.assign(HeaderRoot, {
  Title: HeaderTitleSection,
  Actions: HeaderActionsSection
});
var Main = React3.forwardRef(
  ({ gap = "lg", padding = "lg", skeleton = false, className, ...props }, ref) => {
    const { root } = mainLayout({ gap, padding, skeleton });
    return /* @__PURE__ */ jsx4("main", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Main.displayName = "Main";
var Grid = React3.forwardRef(
  ({
    gap = "md",
    justify = "center",
    align = "start",
    items = "center",
    overflow = "auto",
    flex = "fill",
    padding = "lg",
    className,
    ...props
  }, ref) => {
    const { root } = gridLayout({ gap, justify, align, items, overflow, flex, padding });
    return /* @__PURE__ */ jsx4("section", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Grid.displayName = "Grid";
var Overlay = React3.forwardRef(
  ({ status, origin, pos, elevation, size, children, exited }, ref) => {
    const coords = origin || pos;
    const maskType = origin ? "origin" : pos !== void 0 ? "pos" : void 0;
    const { root } = backdrop({ status, elevation, size });
    return /* @__PURE__ */ jsx4(
      motion2.div,
      {
        ref,
        className: root,
        variants: getBackdropVariants(coords, maskType, exited),
        custom: coords,
        initial: "closed",
        animate: "open",
        exit: "closed",
        children
      }
    );
  }
);
Overlay.displayName = "Overlay";
var getBackdropVariants = (coords, maskType, exited) => {
  const transitions = {
    backgroundSize: {
      duration: DURATION.slow,
      ease: EASING.out
    },
    backdropFilter: {
      ease: EASING.out,
      duration: DURATION.normal
    },
    opacity: {
      duration: DURATION.normal,
      ease: EASING.inOut
    },
    maskImage: {
      duration: DURATION.fast,
      ease: EASING.out
    }
  };
  const baseVariants = {
    open: {
      backgroundSize: "4px 4px",
      backdropFilter: "brightness(0.7) blur(3px)",
      opacity: 1
    },
    closed: {
      backgroundSize: "3px 3px",
      backdropFilter: "brightness(1) blur(5px)",
      opacity: 0
    }
  };
  if (coords === void 0 || maskType === void 0) {
    return {
      open: { ...baseVariants.open },
      closed: { ...baseVariants.closed }
    };
  }
  if (maskType === "origin") {
    return {
      open: ({ x, y }) => ({
        ...baseVariants.open,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 10%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
        transition: {
          ...transitions,
          maskImage: {
            duration: DURATION.slow,
            ease: EASING.in
          }
        }
      }),
      closed: ({ x, y }) => ({
        ...baseVariants.closed,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%, rgba(0,0,0,1) 100%, rgba(0,0,0,1) 100%)`,
        transition: {
          maskImage: {
            duration: DURATION.slow,
            ease: EASING.out
          }
        }
      })
    };
  }
  return {
    open: ({ x, y }) => {
      if (exited) {
        return {
          ...baseVariants.open,
          maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
          transition: {
            ...transitions,
            maskImage: {
              duration: DURATION.slow,
              ease: EASING.in
            }
          }
        };
      }
      return {
        ...baseVariants.open,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
        transition: {
          ...transitions,
          maskImage: {
            duration: DURATION.instant
          }
        }
      };
    },
    closed: ({ x, y }) => {
      if (exited) {
        return {
          ...baseVariants.closed,
          maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
          transition: {
            maskImage: {
              duration: DURATION.slow,
              ease: EASING.out
            }
          }
        };
      }
      return {
        ...baseVariants.closed,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%, rgba(0,0,0,1) 100%, rgba(0,0,0,1) 100%)`,
        transition: {
          maskImage: {
            duration: DURATION.slow,
            ease: EASING.out
          }
        }
      };
    }
  };
};
var ScrollContainer = React3.forwardRef(({ className, ...props }, ref) => {
  const { root } = scrollContainer({ size: "element" });
  return /* @__PURE__ */ jsx4("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
});
ScrollContainer.displayName = "ScrollContainer";

// src/components/button/index.tsx
import React4 from "react";
import { Slot } from "@radix-ui/react-slot";
import { button } from "@pinatagrams/design-system/recipes";
import { motion as motion3 } from "motion/react";
import { useCallback } from "react";

// src/components/button/motion.ts
import { DURATION as DURATION2, OPACITY } from "@pinatagrams/design-system";
var variants = {
  bold: {
    disabled: {
      opacity: OPACITY.disabled
    },
    rest: {
      y: "0"
    },
    rise: {
      y: "-.25rem"
    },
    active: {
      y: "-.25rem",
      transition: {
        duration: DURATION2.rapid
      }
    },
    focus: {
      y: "-.25rem",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  subtle: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  DEFAULT: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  ghost: {
    disabled: {
      opacity: OPACITY.disabled
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  },
  loud: {
    disabled: {
      opacity: OPACITY.disabled
    },
    rest: {
      scaleX: "100%",
      scaleY: "100%"
    },
    rise: {
      scaleX: "110%",
      scaleY: "90%"
    },
    focus: {
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.5)"
    }
  }
};

// src/components/button/sound/click.mp3
var click_default = "data:audio/mpeg;base64,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";

// src/components/button/sound/release.mp3
var release_default = "data:audio/mpeg;base64,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";

// src/components/button/index.tsx
import { Fragment, jsx as jsx5, jsxs as jsxs3 } from "react/jsx-runtime";
var MotionSlot = motion3(Slot);
var Button = React4.forwardRef(
  ({ emphasis, children, size = "md", icons, disabled, asChild, className, style, ...props }, ref) => {
    const { onBlur, onFocus, onMouseDown, onMouseUp } = props;
    const buttonClassName = button({ emphasis, size });
    const mergedClassName = className ? `${buttonClassName} ${className}` : buttonClassName;
    const buttonRef = React4.useRef(null);
    const audioRef = React4.useRef(null);
    const LeftIcon = icons?.[0];
    const RightIcon = icons?.[1];
    const setRefs = useCallback(
      (node) => {
        buttonRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );
    const audioButtonPress = useCallback((state) => {
      audioRef.current = new Audio(state === "click" ? click_default : release_default);
      const playPromise = audioRef.current.play();
      if (playPromise instanceof Promise) {
        playPromise.catch((error) => {
          console.error("Error playing audio:", error);
        });
      }
    }, []);
    const handleOnMouseDown = useCallback(
      (event) => {
        onMouseDown?.(event);
        audioButtonPress("click");
      },
      [onMouseDown, audioButtonPress]
    );
    const handleOnMouseUp = useCallback(
      (event) => {
        onMouseUp?.(event);
        audioButtonPress("release");
      },
      [onMouseUp, audioButtonPress]
    );
    const handleOnFocus = useCallback(
      (event) => {
        onFocus?.(event);
      },
      [onFocus]
    );
    const handleOnBlur = useCallback(
      (event) => {
        onBlur?.(event);
      },
      [onBlur]
    );
    const Component = asChild ? MotionSlot : motion3.button;
    const content = asChild ? children : /* @__PURE__ */ jsxs3(Fragment, { children: [
      LeftIcon && /* @__PURE__ */ jsx5(LeftIcon, {}),
      children,
      RightIcon && /* @__PURE__ */ jsx5(RightIcon, {})
    ] });
    return /* @__PURE__ */ jsx5(
      Component,
      {
        ref: setRefs,
        className: mergedClassName,
        style,
        transition: {
          duration: 0.2,
          ease: "linear"
        },
        variants: variants[emphasis] || variants["DEFAULT"],
        initial: "rest",
        animate: disabled ? "disabled" : "rest",
        whileHover: "rise",
        whileTap: "active",
        whileFocus: "focus",
        layout: "position",
        onMouseDown: handleOnMouseDown,
        onMouseUp: handleOnMouseUp,
        onFocus: handleOnFocus,
        onBlur: handleOnBlur,
        disabled: asChild ? void 0 : disabled,
        ...props,
        children: content
      }
    );
  }
);
Button.displayName = "Button";
var button_default = Button;

// src/components/dropdown-menu/index.tsx
import { Fragment as Fragment2, jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var DropdownMenu = ({
  trigger,
  children,
  open,
  onOpenChange,
  overlay = false,
  ...contentProps
}) => {
  const [isOpen, setIsOpen] = React5.useState(open ?? false);
  const targetLayer = overlay ? 4 /* Overlay */ : 2 /* Popover */;
  const container = useLayerContainer(targetLayer);
  const menuRef = React5.useRef(null);
  const [pos, setPos] = React5.useState(void 0);
  React5.useEffect(() => {
    if (open !== void 0) {
      setIsOpen(open);
    }
  }, [open]);
  const handleOnPlaced = useCallback2(() => {
    if (menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setPos({ x: centerX, y: centerY });
    } else if (!isOpen) {
    }
  }, [menuRef, isOpen]);
  const handleOpenChange = (newOpen) => {
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
  };
  return /* @__PURE__ */ jsxs4(DM.Root, { open: isOpen, onOpenChange: handleOpenChange, children: [
    /* @__PURE__ */ jsx6(DM.Trigger, { asChild: true, children: trigger }),
    /* @__PURE__ */ jsx6(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsx6(DM.Portal, { container, forceMount: true, children: /* @__PURE__ */ jsxs4(Fragment2, { children: [
      overlay && pos && /* @__PURE__ */ jsx6(Overlay, { status: "info", origin: pos }, "overlay"),
      /* @__PURE__ */ jsx6(
        DM.Content,
        {
          sideOffset: 2,
          ...contentProps,
          forceMount: true,
          onPlaced: handleOnPlaced,
          children: /* @__PURE__ */ jsxs4(
            motion4.article,
            {
              className: dropdownMenuContent({}).content,
              initial: { opacity: 0, scale: 0.95 },
              animate: { opacity: 1, scale: 1 },
              exit: { opacity: 0, scale: 0.95 },
              transition: { duration: 0.15 },
              ref: menuRef,
              children: [
                children,
                /* @__PURE__ */ jsx6(DM.Arrow, { className: dropdownMenuContent({}).arrow, width: "1em", height: ".5em" })
              ]
            }
          )
        }
      )
    ] }) }) })
  ] });
};
var DropdownRadioGroup = ({
  header,
  value,
  onChange,
  items,
  placeholder = "No options"
}) => {
  const hasItems = items.length > 0;
  const scale = useMotionValue(1);
  const borderWidth = useTransform(scale, [0.95, 1.05], [3, 1.5]);
  return /* @__PURE__ */ jsxs4(Fragment2, { children: [
    header && /* @__PURE__ */ jsx6(DM.Label, { className: dropdownMenuContent({}).label, children: header }),
    hasItems ? /* @__PURE__ */ jsx6(DM.RadioGroup, { value, onValueChange: onChange, asChild: true, children: /* @__PURE__ */ jsx6(motion4.ol, { className: dropdownMenuContent({}).item, children: items.map((item) => /* @__PURE__ */ jsx6(
      DM.RadioItem,
      {
        value: item.value,
        disabled: item.disabled,
        textValue: item.label,
        asChild: true,
        children: /* @__PURE__ */ jsxs4(button_default, { children: [
          /* @__PURE__ */ jsx6(AnimatePresence, { children: value === item.value && /* @__PURE__ */ jsx6(DM.ItemIndicator, { asChild: true, forceMount: true, children: /* @__PURE__ */ jsxs4("div", { className: dropdownMenuContent({}).indicator, children: [
            /* @__PURE__ */ jsx6(
              motion4.div,
              {
                className: dropdownMenuContent({}).indicatorBorder,
                style: { borderWidth },
                initial: false,
                animate: {
                  borderColor: "#ffff",
                  scale: 1
                },
                exit: {
                  borderColor: "#fff0",
                  scale: 0.95
                },
                transition: {
                  duration: DURATION3.normal,
                  ease: EASING2.out
                }
              }
            ),
            /* @__PURE__ */ jsx6(
              motion4.div,
              {
                className: dropdownMenuContent({}).indicatorDot,
                initial: { scale: 0 },
                animate: {
                  scale: 1,
                  opacity: 1
                },
                exit: {
                  scale: 0,
                  opacity: 0
                },
                transition: SPRING.bouncy
              }
            )
          ] }) }) }),
          item.label,
          item.shortcut && /* @__PURE__ */ jsx6("span", { style: { marginLeft: "auto", opacity: 0.5, fontSize: "0.875em" }, children: item.shortcut })
        ] })
      },
      item.value
    )) }) }) : /* @__PURE__ */ jsx6(DM.Item, { className: dropdownMenuContent({}).item, disabled: true, children: placeholder })
  ] });
};
var DropdownMenuRadioItem = ({ children, ...props }) => {
  return /* @__PURE__ */ jsxs4(DM.RadioItem, { className: dropdownMenuContent({}).item, ...props, children: [
    /* @__PURE__ */ jsx6(DM.ItemIndicator, { className: dropdownMenuContent({}).indicator, children: /* @__PURE__ */ jsx6("span", { children: "\u2713" }) }),
    children
  ] });
};
var Dropdown = Object.assign(DropdownMenu, {
  RadioGroup: DropdownRadioGroup
});
DropdownMenu.displayName = "DropdownMenu";
DropdownRadioGroup.displayName = "DropdownRadioGroup";
DropdownMenuRadioItem.displayName = "DropdownMenuRadioItem";
var dropdown_menu_default = Dropdown;

// src/components/indicator/index.tsx
import { indicator } from "@pinatagrams/design-system/recipes";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var Indicator = ({
  label,
  status = "unavailable",
  icon,
  dropdown,
  overlay,
  ariaLabel
}) => {
  const iconLabel = label ?? ariaLabel ?? "Status";
  const iconElement = /* @__PURE__ */ jsx7("button", { className: indicator({ status }), children: /* @__PURE__ */ jsx7(icon_default, { label: iconLabel, icon: icon || CircleIcon, color: "currentColor", weight: "fill" }) });
  if (dropdown) {
    return /* @__PURE__ */ jsx7(dropdown_menu_default, { overlay, trigger: iconElement, children: dropdown });
  }
  return /* @__PURE__ */ jsxs5(tooltip_default, { children: [
    /* @__PURE__ */ jsx7(tooltip_default.Trigger, { children: iconElement }),
    /* @__PURE__ */ jsx7(tooltip_default.Content, { children: label })
  ] });
};
Indicator.displayName = "Indicator";
var indicator_default = Indicator;
export {
  indicator_default as default
};
//# sourceMappingURL=indicator.js.map